// Wizard de criacao de proposta — disparado a partir da OpportunityDetailPage.
// Steps: 1) Empresa (checkboxes herdar) 2) Items (price table ativa) 3) Condicoes
// Comerciais 4) Anexar Escopo + IA (opcional) 5) Compartilhar (senha + cliente).
//
// Submit: cria proposta -> sobe escopo (se houver) -> compartilha (se senha set).
import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent } from '@/components/ui/Card'
import { Loader2, X, ChevronLeft, ChevronRight, Check, FileText, Building2, Package, FileCheck, Sparkles, Share2, Upload, AlertCircle, Copy, Mail, Lock, Globe, ClipboardCheck } from 'lucide-react'
import { useOpportunity } from '@/hooks/useOpportunities'
import { useCompany } from '@/hooks/useLeads'
import { useActivePriceTables, usePriceTable } from '@/hooks/usePriceTables'
import { useCatalogItems } from '@/hooks/useCatalog'
import { useCreateProposal, useUpdateProposal, useReplaceProposalItems, useUploadProposalScope, useUploadProposalScopeUrl, useUploadProposalScopeText, useShareProposal, useProposalTracking, useLeadAssessments, useBuildScopeFromAssessments, useSetProposalScopeModules, type CommercialConditions, type ScopeModule, type Proposal, type LeadAssessmentItem } from '@/hooks/useProposals'

// Campos da empresa que o wizard permite herdar (subset relevante para proposta).
const COMPANY_FIELDS: { key: string; label: string }[] = [
  { key: 'name', label: 'Razao Social' },
  { key: 'trading_name', label: 'Nome Fantasia' },
  { key: 'cnpj', label: 'CNPJ' },
  { key: 'inscricao_estadual', label: 'Inscricao Estadual' },
  { key: 'inscricao_municipal', label: 'Inscricao Municipal' },
  { key: 'address', label: 'Endereco' },
  { key: 'neighborhood', label: 'Bairro' },
  { key: 'city', label: 'Cidade' },
  { key: 'state', label: 'UF' },
  { key: 'postal_code', label: 'CEP' },
  { key: 'phone', label: 'Telefone' },
  { key: 'email', label: 'E-mail' },
  { key: 'website', label: 'Website' },
  { key: 'industry', label: 'Setor' },
  { key: 'segment', label: 'Segmento' },
  { key: 'company_size', label: 'Porte' },
]


const COUNTRIES_WIZARD: { code: string; label: string; flag: string; currency: string }[] = [
  { code: 'BR',  label: 'Brasil',     flag: '🇧🇷', currency: 'R$'   },
  { code: 'AR',  label: 'Argentina',  flag: '🇦🇷', currency: 'AR$'  },
  { code: 'CL',  label: 'Chile',      flag: '🇨🇱', currency: 'CLP$' },
  { code: 'USD', label: 'Dólar (USD)', flag: '💵', currency: 'US$'  },
  { code: 'UY',  label: 'Uruguai',    flag: '🇺🇾', currency: 'U$'   },
  { code: 'PE',  label: 'Peru',       flag: '🇵🇪', currency: 'S/.'  },
  { code: 'CO',  label: 'Colômbia',   flag: '🇨🇴', currency: 'COP$' },
  { code: 'PY',  label: 'Paraguai',   flag: '🇵🇾', currency: '₲'    },
  { code: 'MX',  label: 'México',     flag: '🇲🇽', currency: 'MX$'  },
  { code: 'BO',  label: 'Bolívia',    flag: '🇧🇴', currency: 'Bs.'  },
]

// Opcoes prontas para os dropdowns de Condicoes Comerciais
const PAYMENT_TERMS = ['Antecipado', '15 dias apos NF', '30 dias apos NF', '45 dias apos NF', '60 dias apos NF', 'Parcelado em 3x', 'Parcelado em 6x', 'Parcelado em 12x']
const CONTRACT_PERIODS = ['12 meses', '24 meses', '36 meses', 'Indeterminado', 'Projeto fechado (escopo unico)']
const PAYMENT_METHODS = ['Boleto bancario', 'PIX', 'Transferencia (TED)', 'Cartao de credito', 'Faturamento']
const ADJUSTMENT_INDEXES = ['IPCA', 'IGPM', 'INPC', 'Sem reajuste']

interface SelectedItem {
  catalog_item_id: string
  price_table_item_id: string
  quantity: number
  discount_percent: number
}

interface WizardState {
  // Step 1: Empresa
  inheritedFields: Record<string, boolean>
  // Step 2: Items
  priceTableId: string | null  // legacy/optional — kept for compat
  country: string  // BR | AR | CL | USD | UY | PE | CO | PY | MX | BO
  selectedItems: Record<string, SelectedItem>  // key: price_table_item_id
  // Step 3: Condicoes Comerciais
  conditions: CommercialConditions
  // Step 4: Escopo (file fica em memoria ate criar a proposta — sera enviado apos)
  scopeFile: File | null
  scopeUrl: string
  scopeText: string
  scopeModulesPreview: ScopeModule[] | null
  // Step 4 (extra): Escopo derivado dos assessments do lead
  useAssessmentScope: boolean
  assessmentSubmissionIds: string[]
  // Step 5: Compartilhamento
  validUntil: string  // YYYY-MM-DD
  notes: string
  shareEnabled: boolean
  sharePassword: string
  sharePasswordHint: string
  shareRecipientName: string
  shareRecipientEmail: string
  shareRecipientPhone: string
  shareSendEmail: boolean
}

const todayPlusDays = (n: number): string => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

export function CreateProposalWizard({ opportunityId, onClose, onCreated, mode = 'create', proposal: existingProposal }: {
  opportunityId: string
  onClose: () => void
  onCreated?: (proposalId: string, publicUrl?: string | null) => void
  mode?: 'create' | 'edit'
  proposal?: Proposal
}) {
  const navigate = useNavigate()
  const isEdit = mode === 'edit' && !!existingProposal
  const { data: opp } = useOpportunity(opportunityId)
  const { data: company } = useCompany(opp?.lead_company_id || null)
  const { data: activeTables } = useActivePriceTables()
  const tracking = useProposalTracking(isEdit ? existingProposal!.id : null)
  const [step, setStep] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [resultPublicUrl, setResultPublicUrl] = useState<string | null>(null)
  const [resultPrivateUrl, setResultPrivateUrl] = useState<string | null>(null)
  const [resultEmailSent, setResultEmailSent] = useState(false)
  // null ate o usuario clicar em "Salvar/Criar" — em edit mode o id ja existe
  // mas nao queremos exibir tela de sucesso antes do submit
  const [createdProposalId, setCreatedProposalId] = useState<string | null>(null)
  const [smtpErrorPopup, setSmtpErrorPopup] = useState<{ ownerEmail: string | null; isSelf: boolean } | null>(null)
  // Link publico — gerado sob demanda na tela de sucesso (acesso interno do time)
  const [publicLinkLoading, setPublicLinkLoading] = useState(false)
  const [publicLinkError, setPublicLinkError] = useState<string | null>(null)

  const createProposal = useCreateProposal()
  const updateProposal = useUpdateProposal()
  const replaceItems = useReplaceProposalItems()
  const uploadScope = useUploadProposalScope()
  const uploadScopeUrl = useUploadProposalScopeUrl()
  const uploadScopeText = useUploadProposalScopeText()
  const shareProposal = useShareProposal()
  const buildScopeAssessments = useBuildScopeFromAssessments()
  const setScopeModules = useSetProposalScopeModules()
  const leadAssessments = useLeadAssessments(opp?.lead_id || null)
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false)
  const [assessmentScopePreviewing, setAssessmentScopePreviewing] = useState(false)

  const [state, setState] = useState<WizardState>(() => {
    if (isEdit && existingProposal) {
      // Pre-fill em edit mode a partir da proposta existente
      const p = existingProposal
      const snapshot = (p.company_snapshot || {}) as Record<string, any>
      const inheritedKeys = Object.keys(snapshot)
      const inherited = Object.fromEntries(COMPANY_FIELDS.map(f => [f.key, inheritedKeys.includes(f.key)]))
      // Items: chave = catalog_item_id (mesma chave usada na lista de produtos da etapa 2)
      const selected: Record<string, SelectedItem> = {}
      for (const it of (p.proposal_items || [])) {
        selected[it.catalog_item_id] = {
          catalog_item_id: it.catalog_item_id,
          price_table_item_id: (it as any).price_table_item_id || '',
          quantity: it.quantity,
          discount_percent: it.discount_percent || 0,
        }
      }
      // Detecta país/moeda da proposta: snapshot.country → fallback BR
      const snapCountry = (snapshot.country || snapshot.country_code || '').toString().toUpperCase()
      const detectedCountry =
        COUNTRIES_WIZARD.find(c => c.code === snapCountry || c.label.toUpperCase() === snapCountry)?.code
        || 'BR'
      // Origem do escopo: prefere submission_ids persistidos; fallback ao filename
      const savedSubmissionIds = (p as any).scope_assessment_submission_ids || []
      const scopeFn = (p.scope_filename || '').toString()
      const hadAssessment = savedSubmissionIds.length > 0 || /assessments?[-_]\d+/i.test(scopeFn)
      // Quando o filename composto inclui URL (ex: "assessments-1 + https://..."),
      // extrai a URL para o campo do wizard
      const urlMatch = scopeFn.match(/(https?:\/\/[^\s]+)/i)
      return {
        inheritedFields: inherited,
        priceTableId: p.price_table_id || null,
        country: detectedCountry,
        selectedItems: selected,
        conditions: {
          payment_term: p.payment_term || '',
          contract_period: p.contract_period || '',
          payment_method: p.payment_method || '',
          adjustment_index: p.adjustment_index || '',
          early_termination_fee_percent: p.early_termination_fee_percent ?? null,
        },
        // Campos de escopo:
        // - scopeFile sempre null (arquivo não persiste em edit mode)
        // - scopeUrl restaurado se filename contém URL
        // - useAssessmentScope marcado se filename indica origem assessment
        scopeFile: null,
        scopeUrl: urlMatch ? urlMatch[1] : '',
        scopeText: p.scope_free_text || '',
        scopeModulesPreview: (p.scope_modules as any) || null,
        useAssessmentScope: hadAssessment,
        assessmentSubmissionIds: savedSubmissionIds,
        validUntil: p.valid_until?.slice(0, 10) || todayPlusDays(30),
        notes: p.notes || '',
        shareEnabled: false,
        sharePassword: '',
        sharePasswordHint: '',
        // Pré-preenchidos via useEffect depois (vê tracking + opp.contact)
        shareRecipientName: '',
        shareRecipientEmail: '',
        shareRecipientPhone: '',
        shareSendEmail: false,
      }
    }
    return {
      inheritedFields: Object.fromEntries(COMPANY_FIELDS.map(f => [f.key, true])),
      priceTableId: null,
      country: 'BR',
      selectedItems: {},
      conditions: { payment_term: '', contract_period: '', payment_method: '', adjustment_index: '', early_termination_fee_percent: null },
      scopeFile: null,
      scopeUrl: '',
      scopeText: '',
      scopeModulesPreview: null,
      useAssessmentScope: false,
      assessmentSubmissionIds: [],
      validUntil: todayPlusDays(30),
      notes: '',
      shareEnabled: false,
      sharePassword: '',
      sharePasswordHint: '',
      shareRecipientName: '',
      shareRecipientEmail: '',
      shareRecipientPhone: '',
      shareSendEmail: false,
    }
  })

  // Em edit mode, pre-fill o destinatario:
  //   1ª prioridade: último link enviado (tracking.data.links)
  //   2ª prioridade: contato/empresa da opportunity associada
  useEffect(() => {
    if (!isEdit) return
    const links = (tracking.data as any)?.links || []
    const lastLink = Array.isArray(links) && links.length > 0
      ? [...links].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
      : null
    setState(s => ({
      ...s,
      shareRecipientName: s.shareRecipientName || lastLink?.recipient_name || opp?.contact_name || '',
      shareRecipientEmail: s.shareRecipientEmail || lastLink?.recipient_email || '',
      shareRecipientPhone: s.shareRecipientPhone || lastLink?.recipient_phone || '',
      sharePasswordHint: s.sharePasswordHint || lastLink?.password_hint || '',
    }))
  }, [isEdit, tracking.data, opp?.contact_name])

  // Quando carrega tabelas ativas, defaulta para a primeira
  useEffect(() => {
    if (!state.priceTableId && activeTables && activeTables.length > 0) {
      setState(s => ({ ...s, priceTableId: activeTables[0].id }))
    }
  }, [activeTables, state.priceTableId])

  const { data: _priceTable } = usePriceTable(state.priceTableId)
  const { data: catalogResp, isLoading: catalogLoading } = useCatalogItems({ per_page: 500, is_active: true })
  const catalogProducts = useMemo(() => {
    const items = (catalogResp?.items || []) as Array<{ id: string; name: string; category?: string | null; prices?: Record<string, number> | null; max_discount_percent?: number | null }>
    // Filtra produtos com preço para o país, mas garante que produtos já selecionados
    // (mesmo sem preço para o país atual em edit mode) continuem visíveis.
    const filtered = items.filter(it => it.prices && Object.prototype.hasOwnProperty.call(it.prices, state.country))
    const selectedIds = new Set(Object.keys(state.selectedItems))
    const filteredIds = new Set(filtered.map(it => it.id))
    const orphans = items.filter(it => selectedIds.has(it.id) && !filteredIds.has(it.id))
    return [...orphans, ...filtered]
  }, [catalogResp, state.country, state.selectedItems])

  // Validacao por step
  const stepValid = useMemo(() => {
    if (step === 1) return true  // checkboxes — aceita 0
    if (step === 2) return Object.keys(state.selectedItems).length > 0
    if (step === 3) {
      const c = state.conditions
      return !!(c.payment_term && c.contract_period && c.payment_method && c.adjustment_index)
    }
    if (step === 4) return true  // opcional
    if (step === 5) {
      if (!state.shareEnabled) return true
      if (!state.shareRecipientName.trim()) return false
      if (state.sharePassword && state.sharePassword.length < 4) return false
      return true
    }
    return true
  }, [step, state])

  const handleSubmit = async () => {
    setError(null)
    setCreating(true)
    try {
      const items = Object.values(state.selectedItems).map(si => ({
        catalog_item_id: si.catalog_item_id,
        price_table_item_id: si.price_table_item_id,
        quantity: si.quantity,
        discount_percent: si.discount_percent,
      }))
      const inheritedKeys = Object.entries(state.inheritedFields).filter(([_, v]) => v).map(([k]) => k)

      let proposalId: string
      if (isEdit && existingProposal) {
        // PUT atualiza condicoes, validade, notes e rebuild company_snapshot
        await updateProposal.mutateAsync({
          proposalId: existingProposal.id,
          data: {
            valid_until: state.validUntil,
            notes: state.notes || undefined,
            commercial_conditions: state.conditions,
            company_fields_to_inherit: inheritedKeys,
          } as any,
        })
        // Substitui items em massa
        await replaceItems.mutateAsync({ proposalId: existingProposal.id, items })
        proposalId = existingProposal.id
      } else {
        // CREATE
        const payload = {
          opportunity_id: opportunityId,
          valid_until: state.validUntil,
          notes: state.notes || undefined,
          price_table_id: state.priceTableId || undefined,
          country: state.country,
          items,
          commercial_conditions: state.conditions,
          company_fields_to_inherit: inheritedKeys,
        }
        const created = await createProposal.mutateAsync(payload as any)
        proposalId = created.id
      }
      setCreatedProposalId(proposalId)

      // Escopo combinado — qualquer combinação das 3 fontes:
      //   assessments + arquivo + URL. Cada fonte gera módulos; o front
      //   concatena (assessments primeiro, depois arquivo, depois URL) e
      //   persiste o resultado final via /scope/set-modules.
      const useAssess = state.useAssessmentScope && state.assessmentSubmissionIds.length > 0
      const hasFile = !!state.scopeFile
      const hasUrl = !!state.scopeUrl.trim()
      const hasText = state.scopeText.trim().length >= 50
      if (useAssess || hasFile || hasUrl || hasText) {
        const combinedModules: ScopeModule[] = []
        const filenameParts: string[] = []
        const textParts: string[] = []

        // 1. Assessments — regera módulos via API (garante consistência
        //    mesmo em edit mode onde o preview pode estar stale).
        if (useAssess) {
          try {
            const res = await buildScopeAssessments.mutateAsync({ submissionIds: state.assessmentSubmissionIds })
            if (res?.modules) combinedModules.push(...res.modules)
            filenameParts.push(`assessments-${state.assessmentSubmissionIds.length}`)
            textParts.push(`Escopo derivado de ${state.assessmentSubmissionIds.length} assessment(s) do lead.`)
          } catch (e: any) {
            // fallback: usa o preview se a regeração falhar
            if (state.scopeModulesPreview && state.scopeModulesPreview.length > 0) {
              combinedModules.push(...state.scopeModulesPreview)
              filenameParts.push(`assessments-${state.assessmentSubmissionIds.length}`)
            }
          }
        }

        // 2. Arquivo — parse via IA (endpoint já persiste, mas vamos sobrescrever no final)
        if (hasFile) {
          try {
            const parsed = await uploadScope.mutateAsync({ proposalId, file: state.scopeFile! })
            if (parsed?.modules) combinedModules.push(...parsed.modules)
            if (state.scopeFile) filenameParts.push(state.scopeFile.name)
            if (parsed?.text_preview) textParts.push(parsed.text_preview)
          } catch (e: any) {
            setError(`Proposta salva, mas falha ao processar arquivo de escopo: ${e?.response?.data?.detail || e?.message || 'erro'}`)
          }
        }

        // 3. URL — idem
        if (hasUrl) {
          try {
            const parsed = await uploadScopeUrl.mutateAsync({ proposalId, url: state.scopeUrl.trim() })
            if (parsed?.modules) combinedModules.push(...parsed.modules)
            filenameParts.push(state.scopeUrl.trim())
            if (parsed?.text_preview) textParts.push(parsed.text_preview)
          } catch (e: any) {
            setError(`Proposta salva, mas falha ao analisar URL: ${e?.response?.data?.detail || e?.message || 'erro'}`)
          }
        }

        // 4. Texto livre — IA estrutura em módulos + o texto integral fica salvo
        if (hasText) {
          try {
            const parsed = await uploadScopeText.mutateAsync({ proposalId, text: state.scopeText })
            if (parsed?.modules) combinedModules.push(...parsed.modules)
            filenameParts.push('texto-livre')
            // Inclui o texto colado integralmente para auditoria
            textParts.push(state.scopeText)
          } catch (e: any) {
            setError(`Proposta salva, mas falha ao processar texto do escopo: ${e?.response?.data?.detail || e?.message || 'erro'}`)
          }
        }

        // 5. Persiste o resultado combinado (sobrescreve eventuais persistências parciais)
        if (combinedModules.length > 0 || hasText) {
          try {
            await setScopeModules.mutateAsync({
              proposalId,
              modules: combinedModules,
              scopeText: textParts.join('\n\n---\n\n'),
              scopeFilename: filenameParts.join(' + ').slice(0, 255),
              submissionIds: useAssess ? state.assessmentSubmissionIds : [],
              freeText: hasText ? state.scopeText : '',
            })
          } catch (e: any) {
            setError(`Proposta salva, mas falha ao combinar escopo: ${e?.response?.data?.detail || e?.message || 'erro'}`)
          }
        }
      }

      // Compartilhamento — gera link publico (sempre) e link privado (se senha set)
      let publicUrl: string | null = null
      let privateUrl: string | null = null
      let emailSent = false

      if (state.shareEnabled && state.shareRecipientName.trim()) {
        // Apenas link PRIVADO — enviado por e-mail imediatamente para o cliente
        // com a senha embutida na mensagem. Link publico fica disponivel como
        // botao opcional na tela de sucesso (acesso interno do time).
        try {
          const privRes: any = await shareProposal.mutateAsync({
            proposalId,
            data: {
              recipient_name: state.shareRecipientName,
              recipient_email: state.shareRecipientEmail || undefined,
              recipient_phone: state.shareRecipientPhone || undefined,
              send_email: !!state.shareRecipientEmail,  // se ha email, envia
              send_whatsapp: false,
              expires_days: 30,
              password: state.sharePassword || undefined,
              password_hint: state.sharePasswordHint || undefined,
            } as any,
          })
          privateUrl = privRes?.public_url || null
          emailSent = !!privRes?.email_sent
        } catch (e: any) {
          const detail = e?.response?.data?.detail
          if (detail && typeof detail === 'object' && detail.code === 'SMTP_NOT_CONFIGURED') {
            setSmtpErrorPopup({ ownerEmail: detail.owner_email || null, isSelf: !!detail.is_self })
            return
          }
          setError(`Proposta salva, mas falha ao enviar para o cliente: ${detail || e?.message || 'erro'}`)
        }
      }

      setResultPublicUrl(publicUrl)
      setResultPrivateUrl(privateUrl)
      setResultEmailSent(emailSent)
      onCreated?.(proposalId, privateUrl || publicUrl)
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code === 'SMTP_NOT_CONFIGURED') {
        setSmtpErrorPopup({ ownerEmail: detail.owner_email || null, isSelf: !!detail.is_self })
        return
      }
      setError(detail || e?.message || (isEdit ? 'Erro ao salvar proposta' : 'Erro ao criar proposta'))
    } finally {
      setCreating(false)
    }
  }

  // Popup quando SMTP do owner nao esta configurado
  if (smtpErrorPopup) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-amber-500/15 p-2 flex-shrink-0"><Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" /></div>
              <div className="flex-1">
                <h3 className="font-bold text-base">SMTP do responsavel nao configurado</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {smtpErrorPopup.isSelf
                    ? <>Voce ainda nao configurou suas credenciais SMTP. Sem isso, propostas nao podem ser enviadas pelo seu e-mail.</>
                    : <>O responsavel pela oportunidade {smtpErrorPopup.ownerEmail ? <b>{smtpErrorPopup.ownerEmail}</b> : ''} ainda nao configurou as credenciais SMTP. Peca pra ele configurar antes de reenviar — ou use o link publico que ja foi gerado.</>
                  }
                </p>
              </div>
            </div>
            <div className="bg-muted/50 p-3 rounded text-xs">
              <strong>Dica:</strong> Para Gmail, gere um <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-primary underline">App Password</a> (com 2FA habilitada na conta) e cole em <em>Perfil → Envio de propostas</em>.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setSmtpErrorPopup(null); onClose() }} className="flex-1">Fechar</Button>
              {smtpErrorPopup.isSelf && (
                <Button onClick={() => { navigate('/profile?tab=smtp'); onClose() }} className="flex-1">
                  <Mail className="h-4 w-4 mr-1" /> Configurar agora
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Gera link publico sob demanda — sem senha, sem destinatario, sem email.
  // Qualquer pessoa logada no CRM ou com a URL pode acessar.
  const generatePublicLink = async () => {
    if (!createdProposalId) return
    setPublicLinkLoading(true); setPublicLinkError(null)
    try {
      const res: any = await shareProposal.mutateAsync({
        proposalId: createdProposalId,
        data: {
          recipient_name: 'Acesso interno (link publico)',
          recipient_email: undefined,
          recipient_phone: undefined,
          send_email: false,
          send_whatsapp: false,
          expires_days: 30,
        } as any,
      })
      setResultPublicUrl(res?.public_url || null)
    } catch (e: any) {
      setPublicLinkError(e?.response?.data?.detail || e?.message || 'Erro ao gerar link publico')
    } finally {
      setPublicLinkLoading(false)
    }
  }

  // Tela final apos sucesso
  if (createdProposalId && !error && (resultPrivateUrl || !state.shareEnabled || resultPublicUrl)) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-6 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 mx-auto flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-center">{isEdit ? 'Proposta atualizada!' : 'Proposta criada!'}</h3>
            <p className="text-sm text-muted-foreground text-center">A proposta {createdProposalId.slice(0, 8)}… foi {isEdit ? 'atualizada' : 'salva como rascunho'}.</p>

            {resultPrivateUrl && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-md space-y-2">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400"><Lock className="h-3 w-3" /> Link privado {state.sharePassword ? '(com senha)' : ''} — enviado ao cliente</div>
                <div className="flex items-center gap-2">
                  <Input value={resultPrivateUrl} readOnly className="text-xs font-mono" />
                  <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(resultPrivateUrl)} title="Copiar"><Copy className="h-3 w-3" /></Button>
                </div>
                {state.sharePassword && <div className="text-xs text-amber-700 dark:text-amber-400">Senha: <code className="font-mono bg-background/50 px-1 py-0.5 rounded">{state.sharePassword}</code></div>}
                {resultEmailSent
                  ? <div className="text-xs text-emerald-600 dark:text-emerald-400">✓ E-mail enviado para {state.shareRecipientEmail}</div>
                  : state.shareRecipientEmail
                    ? <div className="text-xs text-amber-700 dark:text-amber-400">⚠ E-mail nao foi enviado (cheque SMTP)</div>
                    : null
                }
              </div>
            )}

            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-blue-500" /> Link público interno</div>
                  <div className="text-xs text-muted-foreground">Sem senha — para o time logado no CRM ou compartilhamento aberto.</div>
                </div>
                {!resultPublicUrl && (
                  <Button size="sm" variant="outline" onClick={generatePublicLink} disabled={publicLinkLoading}>
                    {publicLinkLoading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Gerando…</> : <><Globe className="h-3.5 w-3.5 mr-1" /> Gerar link público</>}
                  </Button>
                )}
              </div>
              {resultPublicUrl && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-2.5 rounded-md space-y-1">
                  <div className="flex items-center gap-2">
                    <Input value={resultPublicUrl} readOnly className="text-xs font-mono" />
                    <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(resultPublicUrl)} title="Copiar"><Copy className="h-3 w-3" /></Button>
                  </div>
                </div>
              )}
              {publicLinkError && <div className="text-xs text-destructive">{publicLinkError}</div>}
            </div>

            <Button className="w-full" onClick={onClose}>Fechar</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold">{isEdit ? `Editar Proposta v${existingProposal!.version}` : 'Criar Proposta'}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Oportunidade: {opp?.name || '—'} · Cliente: {opp?.lead_company_name || '—'}
            </p>
          </div>
          <button className="p-1 rounded hover:bg-muted" onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between gap-2 px-6 py-3 border-b bg-muted/30 text-xs">
          {[
            { n: 1, icon: Building2, label: 'Empresa' },
            { n: 2, icon: Package, label: 'Items' },
            { n: 3, icon: FileCheck, label: 'Condicoes' },
            { n: 4, icon: Sparkles, label: 'Escopo (IA)' },
            { n: 5, icon: Share2, label: 'Compartilhar' },
          ].map(({ n, icon: Icon, label }) => (
            <div key={n} className={`flex items-center gap-1.5 flex-1 ${step === n ? 'text-primary font-semibold' : step > n ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              <div className={`h-6 w-6 rounded-full flex items-center justify-center ${step === n ? 'bg-primary text-primary-foreground' : step > n ? 'bg-emerald-500 text-white' : 'bg-muted'}`}>
                {step > n ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
              </div>
              <span className="hidden md:inline">{label}</span>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">Dados da empresa para herdar</h3>
                <p className="text-sm text-muted-foreground">Marque os campos que devem aparecer na proposta. Os valores sao "congelados" no momento da criacao (snapshot).</p>
              </div>
              {!company ? (
                <div className="text-sm text-muted-foreground italic">Carregando empresa…</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {COMPANY_FIELDS.map(f => {
                    const value = (company as any)[f.key]
                    const checked = state.inheritedFields[f.key]
                    return (
                      <label key={f.key} className={`flex items-start gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50 ${checked ? 'border-primary/40 bg-primary/5' : 'border-transparent'}`}>
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={(e) => setState(s => ({ ...s, inheritedFields: { ...s.inheritedFields, [f.key]: e.target.checked } }))}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium">{f.label}</div>
                          <div className="text-sm truncate">{value || <span className="text-muted-foreground italic">(vazio)</span>}</div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">Selecionar produtos da proposta</h3>
                <p className="text-sm text-muted-foreground">Escolha o país para definir a moeda/preço, depois marque os produtos e ajuste quantidade/desconto.</p>
              </div>
              {/* === Country selector === */}
              <div>
                <label className="text-xs text-muted-foreground">País / moeda</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                  value={state.country}
                  onChange={(e) => setState(s => ({ ...s, country: e.target.value, selectedItems: {} }))}
                >
                  {COUNTRIES_WIZARD.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.label} ({c.currency})</option>
                  ))}
                </select>
              </div>
              {/* === Catalog-driven product list filtered by country === */}
              {catalogLoading ? (
                <div className="text-sm text-muted-foreground italic flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando produtos…</div>
              ) : catalogProducts.length === 0 ? (
                <div className="text-sm text-muted-foreground italic p-4 rounded border">
                  Nenhum produto possui preço cadastrado para {COUNTRIES_WIZARD.find(c => c.code === state.country)?.label || state.country}. Cadastre o preço no Catálogo de Produtos antes de continuar.
                </div>
              ) : (
                <div className="space-y-2">
                  {catalogProducts.map(it => {
                    const sel = state.selectedItems[it.id]
                    const priceForCountry = it.prices?.[state.country] ?? 0
                    const maxDisc = it.max_discount_percent ?? 0
                    const currency = COUNTRIES_WIZARD.find(c => c.code === state.country)?.currency || 'R$'
                    return (
                      <div key={it.id} className={`p-3 rounded border ${sel ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!sel}
                            onChange={(e) => {
                              setState(s => {
                                const next = { ...s.selectedItems }
                                if (e.target.checked) {
                                  next[it.id] = { catalog_item_id: it.id, price_table_item_id: '', quantity: 1, discount_percent: 0 }
                                } else {
                                  delete next[it.id]
                                }
                                return { ...s, selectedItems: next }
                              })
                            }}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{it.name}{it.category && <span className="text-xs text-muted-foreground ml-2">· {it.category}</span>}</div>
                            <div className="text-xs text-muted-foreground">
                              {currency} {priceForCountry.toFixed(2).replace('.', ',')} / unidade · max desconto {maxDisc}%{maxDisc > 0 ? ' sem aprovação' : ''}
                            </div>
                          </div>
                        </label>
                        {sel && (
                          <div className="grid grid-cols-2 gap-2 mt-2 ml-6">
                            <div>
                              <label className="text-xs text-muted-foreground">Quantidade</label>
                              <Input type="number" min={1} value={sel.quantity}
                                onChange={(e) => setState(s => ({ ...s, selectedItems: { ...s.selectedItems, [it.id]: { ...sel, quantity: parseInt(e.target.value || '1') || 1 } } }))}
                                className="h-8 text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">
                                Desconto (%) {sel.discount_percent > maxDisc && <span className="text-amber-600 dark:text-amber-400 ml-1">⚠ requer aprovação CFO/CEO</span>}
                              </label>
                              <Input type="number" min={0} max={100} step="0.5" value={sel.discount_percent}
                                onChange={(e) => setState(s => ({ ...s, selectedItems: { ...s.selectedItems, [it.id]: { ...sel, discount_percent: parseFloat(e.target.value || '0') || 0 } } }))}
                                className="h-8 text-sm" />
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">Condicoes Comerciais</h3>
                <p className="text-sm text-muted-foreground">Todos os campos sao obrigatorios. Aparecerao na secao "Condicoes de Pagamento" da proposta.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Prazo de pagamento *</label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                    value={state.conditions.payment_term || ''}
                    onChange={(e) => setState(s => ({ ...s, conditions: { ...s.conditions, payment_term: e.target.value } }))}>
                    <option value="">— selecione —</option>
                    {PAYMENT_TERMS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Periodo de contrato *</label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                    value={state.conditions.contract_period || ''}
                    onChange={(e) => setState(s => ({ ...s, conditions: { ...s.conditions, contract_period: e.target.value } }))}>
                    <option value="">— selecione —</option>
                    {CONTRACT_PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Forma de pagamento *</label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                    value={state.conditions.payment_method || ''}
                    onChange={(e) => setState(s => ({ ...s, conditions: { ...s.conditions, payment_method: e.target.value } }))}>
                    <option value="">— selecione —</option>
                    {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Reajuste anual *</label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                    value={state.conditions.adjustment_index || ''}
                    onChange={(e) => setState(s => ({ ...s, conditions: { ...s.conditions, adjustment_index: e.target.value } }))}>
                    <option value="">— selecione —</option>
                    {ADJUSTMENT_INDEXES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Multa rescisoria (%)</label>
                  <Input type="number" min={0} max={100} step="0.5"
                    value={state.conditions.early_termination_fee_percent ?? ''}
                    onChange={(e) => setState(s => ({ ...s, conditions: { ...s.conditions, early_termination_fee_percent: e.target.value === '' ? null : parseFloat(e.target.value) } }))}
                    placeholder="Ex: 30"
                    className="h-9 text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Validade da proposta</label>
                  <Input type="date" value={state.validUntil}
                    onChange={(e) => setState(s => ({ ...s, validUntil: e.target.value }))}
                    className="h-9 text-sm mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Observacoes (opcional)</label>
                <textarea className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                  value={state.notes}
                  onChange={(e) => setState(s => ({ ...s, notes: e.target.value }))}
                  placeholder="Notas internas — nao aparecem na proposta visual." />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">Anexar escopo (opcional)</h3>
                <p className="text-sm text-muted-foreground">Combine quantas fontes quiser: derive dos assessments respondidos pelo lead, anexe um arquivo e/ou cole um link público. Todos os módulos serão concatenados no documento final.</p>
              </div>

              {/* Em edit mode, indica o escopo atualmente salvo */}
              {isEdit && existingProposal?.scope_filename && (
                <div className="text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-3 rounded flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Escopo atual:</strong> {existingProposal.scope_filename}
                    {existingProposal.scope_modules && existingProposal.scope_modules.length > 0 && (
                      <span className="ml-1">— {existingProposal.scope_modules.length} módulo(s) processado(s)</span>
                    )}
                    <div className="text-[10px] opacity-80 mt-1">Para substituir, escolha uma nova fonte abaixo. Para manter, deixe os campos vazios e avance.</div>
                  </div>
                </div>
              )}

              {/* Opcao 0: Escopo derivado de assessments do lead */}
              <div className={`border-2 rounded-lg p-4 ${state.useAssessmentScope ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={state.useAssessmentScope}
                    onChange={(e) => {
                      const enabled = e.target.checked
                      setState(s => ({
                        ...s,
                        useAssessmentScope: enabled,
                        // limpa ids/preview ao desmarcar
                        assessmentSubmissionIds: enabled ? s.assessmentSubmissionIds : [],
                        scopeModulesPreview: enabled ? s.scopeModulesPreview : null,
                      }))
                      if (enabled) setAssessmentModalOpen(true)
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm">Considerar assessments do lead como escopo</span>
                      {leadAssessments.data && leadAssessments.data.length > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-muted rounded-full">
                          {leadAssessments.data.length} disponível{leadAssessments.data.length !== 1 ? 'is' : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Lê os conectores de bases autoritativas (IdP, RH) e sistemas alvo informados nos assessments e gera os módulos automaticamente — bases autoritativas primeiro, depois sistemas alvo.
                    </p>
                    {state.useAssessmentScope && state.assessmentSubmissionIds.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {state.assessmentSubmissionIds.length} assessment(s) selecionado(s)
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setAssessmentModalOpen(true) }}
                          className="text-xs text-primary hover:underline"
                        >
                          Alterar seleção
                        </button>
                      </div>
                    )}
                    {state.useAssessmentScope && state.assessmentSubmissionIds.length === 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setAssessmentModalOpen(true) }}
                        className="text-xs text-primary hover:underline mt-2"
                      >
                        Selecionar assessments →
                      </button>
                    )}
                    {state.useAssessmentScope && state.scopeModulesPreview && state.scopeModulesPreview.length > 0 && (
                      <div className="mt-3 border rounded p-2 bg-card max-h-48 overflow-y-auto">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                          Preview dos módulos derivados ({state.scopeModulesPreview.length})
                        </div>
                        <div className="space-y-1">
                          {state.scopeModulesPreview.map((m, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs">
                              <span className="flex-shrink-0">{m.icon}</span>
                              <div>
                                <div className="font-medium">{m.name}</div>
                                <div className="text-[10px] text-muted-foreground">{m.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Divisor "OU" */}
              {!state.useAssessmentScope && (
                <div className="relative flex items-center py-1">
                  <div className="flex-1 border-t border-border"></div>
                  <span className="px-3 text-xs text-muted-foreground uppercase tracking-wider">ou anexe um documento</span>
                  <div className="flex-1 border-t border-border"></div>
                </div>
              )}

              {/* Opcao 1: upload de arquivo */}
              <label className={`block border-2 border-dashed rounded-lg p-5 text-center cursor-pointer hover:bg-muted/30 transition-colors ${state.scopeFile ? 'border-primary/40 bg-primary/5' : ''}`}>
                <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null
                    setState(s => ({ ...s, scopeFile: f }))
                  }} />
                <Upload className="h-7 w-7 mx-auto mb-1.5 text-muted-foreground" />
                <div className="text-sm font-medium">{state.scopeFile ? state.scopeFile.name : 'Clique para anexar arquivo'}</div>
                <div className="text-xs text-muted-foreground mt-1">PDF · DOCX · TXT · MD (max 10 MB)</div>
              </label>

              {/* Divisor "E/OU" */}
              <div className="relative flex items-center py-1">
                <div className="flex-1 border-t border-border"></div>
                <span className="px-3 text-xs text-muted-foreground uppercase tracking-wider">e/ou</span>
                <div className="flex-1 border-t border-border"></div>
              </div>

              {/* Opcao 2: URL publica */}
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Globe className="h-3 w-3" /> Cole um link publico (HTML/PDF/TXT acessivel sem login)
                </label>
                <Input
                  type="url"
                  value={state.scopeUrl}
                  onChange={(e) => setState(s => ({ ...s, scopeUrl: e.target.value }))}
                  placeholder="https://exemplo.com/escopo-projeto.html"
                  className="h-9 text-sm mt-1 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Funciona com paginas publicas (Google Docs publicado, Notion publico, README do GitHub raw, PDF hospedado, etc.). Privadas/com login nao funcionam.
                </p>
              </div>

              {/* Divisor "E/OU" */}
              <div className="relative flex items-center py-1">
                <div className="flex-1 border-t border-border"></div>
                <span className="px-3 text-xs text-muted-foreground uppercase tracking-wider">e/ou</span>
                <div className="flex-1 border-t border-border"></div>
              </div>

              {/* Opcao 3: Texto livre */}
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <FileText className="h-3 w-3" /> Cole o texto do escopo (a IA estrutura em módulos)
                </label>
                <textarea
                  value={state.scopeText}
                  onChange={(e) => setState(s => ({ ...s, scopeText: e.target.value }))}
                  placeholder="Cole aqui o texto do escopo do projeto — funcionalidades, requisitos, módulos, processos. A IA do CRM organiza em cards de módulos no documento final."
                  rows={6}
                  className="w-full text-sm mt-1 rounded-md border border-input bg-background px-3 py-2 leading-relaxed resize-y min-h-[120px]"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-muted-foreground">
                    Mínimo 50 caracteres. O texto integral fica salvo na proposta para auditoria.
                  </p>
                  <span className={`text-xs ${state.scopeText.trim().length >= 50 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {state.scopeText.trim().length} caracteres
                  </span>
                </div>
              </div>

              {(state.scopeFile || state.scopeUrl.trim()) && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 p-3 rounded flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>O processamento pela IA acontece <strong>apos {isEdit ? 'salvar' : 'criar'} a proposta</strong> (~30-60s). Voce vera os modulos estruturados na tela seguinte.</div>
                </div>
              )}

              {state.scopeModulesPreview && state.scopeModulesPreview.length > 0 && !state.scopeFile && !state.scopeUrl.trim() && (
                <div className="border rounded-lg p-3 bg-muted/30">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Modulos atuais (escopo ja processado)</div>
                  <div className="space-y-1.5">
                    {state.scopeModulesPreview.map((m, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="flex-shrink-0">{m.icon}</span>
                        <div>
                          <div className="font-medium">{m.name}</div>
                          <div className="text-xs text-muted-foreground">{m.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">Compartilhamento (opcional)</h3>
                <p className="text-sm text-muted-foreground">Crie ja o link privado com senha para enviar ao cliente. Se desmarcar, voce pode compartilhar depois pela tela de Propostas.</p>
              </div>
              <label className="flex items-center gap-2 p-3 border rounded cursor-pointer hover:bg-muted/30">
                <input type="checkbox" checked={state.shareEnabled}
                  onChange={(e) => setState(s => ({ ...s, shareEnabled: e.target.checked }))} />
                <span className="font-medium text-sm">Gerar link privado para o cliente</span>
              </label>
              {state.shareEnabled && (
                <div className="space-y-3 pl-2 border-l-2 border-primary/30">
                  <div>
                    <label className="text-xs text-muted-foreground">Nome do destinatario *</label>
                    <Input value={state.shareRecipientName}
                      onChange={(e) => setState(s => ({ ...s, shareRecipientName: e.target.value }))}
                      placeholder="Ex: Maria Silva (Diretora de TI)" className="h-9 text-sm mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">E-mail</label>
                      <Input type="email" value={state.shareRecipientEmail}
                        onChange={(e) => setState(s => ({ ...s, shareRecipientEmail: e.target.value }))}
                        className="h-9 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Telefone/WhatsApp</label>
                      <Input value={state.shareRecipientPhone}
                        onChange={(e) => setState(s => ({ ...s, shareRecipientPhone: e.target.value }))}
                        placeholder="Ex: 11 99999-9999" className="h-9 text-sm mt-1" />
                    </div>
                  </div>
                  {state.shareRecipientEmail && (
                    <div className="flex items-start gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-2.5 rounded">
                      <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div>O link privado sera <b>enviado automaticamente</b> para <b>{state.shareRecipientEmail}</b> com a senha embutida no e-mail (se preenchida abaixo).</div>
                    </div>
                  )}
                  <div className="border-t pt-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Senha (opcional)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Senha de acesso</label>
                        <Input type="text" value={state.sharePassword}
                          onChange={(e) => setState(s => ({ ...s, sharePassword: e.target.value }))}
                          placeholder="Ex: cliente#2026" className="h-9 text-sm mt-1 font-mono" />
                        {state.sharePassword && state.sharePassword.length < 4 && <div className="text-xs text-destructive mt-1">Min. 4 caracteres</div>}
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Dica (opcional)</label>
                        <Input value={state.sharePasswordHint}
                          onChange={(e) => setState(s => ({ ...s, sharePasswordHint: e.target.value }))}
                          placeholder="Ex: nome da empresa em minusculas" className="h-9 text-sm mt-1" />
                      </div>
                    </div>
                    {state.sharePassword && <div className="text-xs text-muted-foreground mt-2">Quando setada, o link mostra uma tela de bloqueio igual ao padrao do portfolio (viamao). Senha e validada client-side via SHA-256.</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t bg-muted/20">
          <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : onClose()}>
            <ChevronLeft className="h-4 w-4 mr-1" /> {step === 1 ? 'Cancelar' : 'Voltar'}
          </Button>
          <div className="text-xs text-muted-foreground">Passo {step} de 5</div>
          {step < 5 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!stepValid}>
              Avancar <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!stepValid || creating}>
              {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {isEdit ? 'Salvando…' : 'Criando…'}</> : <><FileText className="h-4 w-4 mr-1" /> {isEdit ? 'Salvar alteracoes' : 'Criar Proposta'}</>}
            </Button>
          )}
        </div>
      </div>

      {assessmentModalOpen && (
        <AssessmentScopeModal
          assessments={leadAssessments.data || []}
          loading={leadAssessments.isLoading}
          selectedIds={state.assessmentSubmissionIds}
          onClose={() => setAssessmentModalOpen(false)}
          onConfirm={async (ids) => {
            setAssessmentScopePreviewing(true)
            try {
              const res = await buildScopeAssessments.mutateAsync({ submissionIds: ids })
              setState(s => ({
                ...s,
                assessmentSubmissionIds: ids,
                scopeModulesPreview: res.modules,
              }))
            } catch (e: any) {
              setError(e?.response?.data?.detail || 'Falha ao gerar preview dos módulos')
            } finally {
              setAssessmentScopePreviewing(false)
              setAssessmentModalOpen(false)
            }
          }}
          previewing={assessmentScopePreviewing}
        />
      )}
    </div>
  )
}

// ─── Modal: seleção de assessments para escopo ─────────────────────────────────

function AssessmentScopeModal({
  assessments, loading, selectedIds, onClose, onConfirm, previewing,
}: {
  assessments: LeadAssessmentItem[]
  loading: boolean
  selectedIds: string[]
  onClose: () => void
  onConfirm: (ids: string[]) => void | Promise<void>
  previewing: boolean
}) {
  const [picked, setPicked] = useState<string[]>(selectedIds)

  function toggle(id: string) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])
  }

  const eligible = assessments.filter(a => a.has_directories || a.has_targets)

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Assessments do lead</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Selecione os assessments cujos conectores devem entrar no escopo desta proposta.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando assessments…
            </div>
          ) : assessments.length === 0 ? (
            <div className="text-sm text-muted-foreground italic text-center py-8">
              Nenhum assessment encontrado para este lead.
            </div>
          ) : (
            <>
              {eligible.length === 0 && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 p-2 rounded flex items-start gap-2 mb-2">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <div>Nenhum dos assessments deste lead possui conectores informados (bases autoritativas/sistemas alvo).</div>
                </div>
              )}
              {assessments.map((a) => {
                const checked = picked.includes(a.id)
                const isEligible = a.has_directories || a.has_targets
                return (
                  <label
                    key={a.id}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      checked ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/30'
                    } ${!isEligible ? 'opacity-60' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={checked}
                      onChange={() => toggle(a.id)}
                      disabled={!isEligible}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{a.assessment_name || a.pillar}</span>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-muted rounded">
                          {a.pillar}
                        </span>
                        {a.maturity_band && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded">{a.maturity_band}</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {a.company_name} · {a.contact_name} · {new Date(a.submitted_at || a.created_at).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-xs mt-1.5 flex gap-3">
                        <span className={a.has_directories ? 'text-emerald-600' : 'text-muted-foreground'}>
                          {a.has_directories ? '✓' : '○'} {a.directories_count} bases autoritativas
                        </span>
                        <span className={a.has_targets ? 'text-emerald-600' : 'text-muted-foreground'}>
                          {a.has_targets ? '✓' : '○'} {a.targets_count} sistemas alvo
                        </span>
                      </div>
                    </div>
                  </label>
                )
              })}
            </>
          )}
        </div>

        <div className="p-3 border-t bg-muted/30 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {picked.length} de {assessments.length} selecionado{picked.length !== 1 ? 's' : ''}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={previewing}>Cancelar</Button>
            <Button
              size="sm"
              onClick={() => onConfirm(picked)}
              disabled={picked.length === 0 || previewing}
            >
              {previewing ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Gerando…</> : <>Confirmar seleção</>}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
